/**
 * DesignerShell — the canonical 6-slot designer layout.
 *
 * Phase 13 P0 (June 2026)
 * =======================
 *
 * Responsibility
 * --------------
 *
 *   1. Pull the registered DesignerTools that apply to the current
 *      designer context (via the registry's `listBySlot(ctx)`).
 *   2. Render each slot (TOP_TOOLBAR / LEFT_RAIL / CANVAS_OVERLAY /
 *      RIGHT_INSP / BOTTOM_STRIP / MODAL_MOUNT) with the tools that
 *      bound to that slot.
 *   3. Wrap each tool's Component in `<ToolMount>` — an error
 *      boundary so a single buggy tool can't take down the entire
 *      shell.
 *   4. Render the canvas surface in the middle. The shell does NOT
 *      know what the canvas IS — it accepts a `canvasSlot` prop
 *      (typically a renderer like `<ThreeGame>` / `<SceneCanvas>` /
 *      `<VideoPlayer>`). Tools that need to overlay the canvas
 *      mount in CANVAS_OVERLAY and read the canvas-ref from the
 *      designer context.
 *
 * What the shell is NOT
 * ---------------------
 *
 *   * It is not a renderer. It does not know that football scenes
 *     use ThreeGame and slide_deck scenes use a PdfViewer. The page
 *     decides the canvas; the shell just composes slots.
 *   * It is not a scene loader. The page already loaded the scene
 *     into `sceneStore` (via `loadScene()`) before mounting the
 *     shell. The shell only reads from context.
 *   * It is not a router. Page-level routing stays where it is
 *     today — `react-router-dom` decides which DesignerShell mounts
 *     for which URL.
 *
 * Layout
 * ------
 *
 *     ┌──────────────────────────────────────────────────────────┐
 *     │ TOP_TOOLBAR                                              │
 *     ├────────┬──────────────────────────────────────┬──────────┤
 *     │ LEFT_  │            CANVAS                    │ RIGHT_   │
 *     │ RAIL   │ (page's canvasSlot, plus overlay     │ INSP     │
 *     │        │  tools rendered absolutely)          │          │
 *     │        │                                      │          │
 *     ├────────┴──────────────────────────────────────┴──────────┤
 *     │ BOTTOM_STRIP                                             │
 *     └──────────────────────────────────────────────────────────┘
 *     [MODAL_MOUNT — fullscreen-overlay portal target, mounted last]
 *
 * Empty-slot policy: a slot with zero tools collapses (no border,
 * no background, no fixed min-size). This keeps the layout clean
 * for scene types that don't need every slot.
 *
 * @see DesignerToolRegistry — listBySlot()
 * @see DesignerContext      — useDesigner()
 * @see designer-shell.css   — the 6-slot grid implementation
 */

import React, { useMemo } from 'react';

import './designer-shell.css';
import { DESIGNER_SLOTS } from './DesignerTool';
import { useDesigner } from './DesignerContext';
import { listBySlot } from './DesignerToolRegistry';

// ────────────────────────────────────────────────────────────────────
// ToolMount — per-tool error boundary.
// ────────────────────────────────────────────────────────────────────
//
// React error boundaries must be class components today. The
// boundary catches render-time errors only; effect-time errors
// propagate to the global handler and are not surfaced here. That's
// the right tradeoff: most tool bugs are render-time
// (undefined.actor_id), and effect-time bugs usually want to crash
// loudly so the dev sees them.

class ToolMount extends React.Component {
  /** @param {{ tool: import('./DesignerTool').DesignerTool, ctx: any }} props */
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(
      `[DesignerShell] tool ${this.props.tool?.id} crashed:`,
      error, info,
    );
  }

  render() {
    const { tool } = this.props;
    if (this.state.error) {
      // Inline error pill — does not block the rest of the shell.
      return (
        <div
          className="designer-tool-error"
          role="status"
          data-tool-id={tool.id}
          data-tool-error="1"
          aria-label={`Designer tool ${tool.id} failed to render`}
          title={String(this.state.error?.message || this.state.error)}
        >
          ⚠ {tool.label} unavailable
        </div>
      );
    }

    if (!tool.Component) {
      // Tools without a Component are pseudo-tools (dividers,
      // headers); treat as no-op rather than crashing.
      return null;
    }

    // Phase 13 P5 — wrap every mounted tool in a stable
    // `data-tool-id` envelope so e2e tests + integration
    // assertions can find them without leaking the tool's
    // internal class names. The wrapper is a `<div>` with
    // `display: contents` so it doesn't disturb flex / grid
    // layouts that the parent slot owns.
    return (
      <div
        data-tool-id={tool.id}
        data-tool-slot={tool.slot}
        style={{ display: 'contents' }}
      >
        <tool.Component />
      </div>
    );
  }
}

// ────────────────────────────────────────────────────────────────────
// Slot wrapper.
// ────────────────────────────────────────────────────────────────────

function Slot({ name, tools, ctx }) {
  if (!tools || tools.length === 0) {
    // Render nothing for empty slots so the grid auto-collapses.
    return null;
  }

  return (
    <div
      className={`designer-slot designer-slot--${name.toLowerCase()}`}
      data-slot={name}
      data-tool-count={tools.length}
    >
      {tools.map((t) => (
        <ToolMount key={t.id} tool={t} ctx={ctx} />
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Public component.
// ────────────────────────────────────────────────────────────────────

/**
 * @param {Object} props
 * @param {React.ReactNode=} props.canvasSlot
 *   The page's renderer surface — typically a Three.js / Konva /
 *   Phaser host or a Reading-pane component. Mounted in the centre
 *   of the grid; CANVAS_OVERLAY tools render on top.
 * @param {string=} props.className
 *   Optional class for the outer container. The shell uses
 *   `designer-shell` by default; pages adding tenant-themed
 *   wrappers can compose.
 * @param {React.ReactNode=} props.fallbackEmpty
 *   Rendered inside the canvas slot when no scene is loaded.
 *   Default: a centered "Loading scene…" placeholder.
 */
export function DesignerShell({
  canvasSlot,
  className = '',
  fallbackEmpty = null,
}) {
  const ctx = useDesigner();
  const bySlot = useMemo(() => listBySlot(ctx), [ctx]);

  // Pre-extract for readability — JSX below would be a wall of map().
  const top      = bySlot.TOP_TOOLBAR;
  const left     = bySlot.LEFT_RAIL;
  const overlay  = bySlot.CANVAS_OVERLAY;
  const right    = bySlot.RIGHT_INSP;
  const bottom   = bySlot.BOTTOM_STRIP;
  const modal    = bySlot.MODAL_MOUNT;

  return (
    <div className={`designer-shell ${className}`} data-shell-version="v2">
      <Slot name="TOP_TOOLBAR" tools={top} ctx={ctx} />

      <div className="designer-shell__body">
        <Slot name="LEFT_RAIL" tools={left} ctx={ctx} />

        <div className="designer-shell__canvas-wrap">
          {canvasSlot
            ? canvasSlot
            : (
              fallbackEmpty || (
                <div className="designer-shell__empty">
                  {ctx.scene ? null : 'Loading scene…'}
                </div>
              )
            )}
          {overlay.length > 0 && (
            // Overlay slot positions tools absolutely over the canvas
            // so they don't displace the renderer's layout. Tools
            // inside this slot are responsible for their own
            // pointer-events policy.
            <div
              className="designer-slot designer-slot--canvas_overlay"
              data-slot="CANVAS_OVERLAY"
              data-tool-count={overlay.length}
            >
              {overlay.map((t) => (
                <ToolMount key={t.id} tool={t} ctx={ctx} />
              ))}
            </div>
          )}
        </div>

        <Slot name="RIGHT_INSP" tools={right} ctx={ctx} />
      </div>

      <Slot name="BOTTOM_STRIP" tools={bottom} ctx={ctx} />

      {/* MODAL_MOUNT is always rendered last so its tools sit above
          the rest of the shell in z-order without manually managing
          z-index. */}
      {modal.length > 0 && (
        <div
          className="designer-slot designer-slot--modal_mount"
          data-slot="MODAL_MOUNT"
          data-tool-count={modal.length}
        >
          {modal.map((t) => (
            <ToolMount key={t.id} tool={t} ctx={ctx} />
          ))}
        </div>
      )}
    </div>
  );
}

// Internal export so tests can render ToolMount directly when
// asserting error-boundary behavior.
export const _ToolMountForTests = ToolMount;

// Re-export the canonical slot list for consumers that want to
// iterate or assert against it.
export { DESIGNER_SLOTS };

export default DesignerShell;
